import type {
  InheritedSecret,
  OrgSecret,
  OrgSecretScope,
} from './maestro-org-api';
import { scopeTarget, type ScopeTarget } from './org-secret-scope';

/** Who holds a row: this org, a Pypestream key, or a parent org's own secret. */
export type SecretSource = 'own' | 'pypestreamKey' | 'parent';

export interface OrgSecretRow {
  key: string;
  name: string;
  kind: OrgSecret['kind'];
  source: SecretSource;
  scope: OrgSecretScope;
  /** Null for an inherited row: only its holder can change it. */
  secret: OrgSecret | null;
}

export interface SecretFilters {
  search: string;
  type: 'all' | OrgSecret['kind'];
  scope: 'all' | ScopeTarget;
  source: 'all' | SecretSource;
}

export const NO_FILTERS: SecretFilters = {
  search: '',
  type: 'all',
  scope: 'all',
  source: 'all',
};

const ORG_SCOPE: OrgSecretScope = {
  type: 'org',
  project: null,
  application: null,
  agent: null,
};

const ownRow = (secret: OrgSecret): OrgSecretRow => ({
  key: `own:${secret.id}`,
  name: secret.name,
  kind: secret.kind,
  source: secret.pypestreamKey ? 'pypestreamKey' : 'own',
  scope: secret.scope,
  secret,
});

/**
 * The org's own rows at every scope and those it inherits, as one list sorted by
 * name. On the root org a Pypestream key is still its own row, so it stays editable there.
 */
export function orgSecretRows(
  own: OrgSecret[],
  scoped: OrgSecret[],
  inherited: InheritedSecret[]
): OrgSecretRow[] {
  const rows: OrgSecretRow[] = [
    ...own.map(ownRow),
    ...scoped.map(ownRow),
    ...inherited.map((secret) => ({
      key: `inherited:${secret.accountId ?? ''}:${secret.name}`,
      name: secret.name,
      kind: secret.kind,
      source: (secret.pypestreamKey
        ? 'pypestreamKey'
        : 'parent') as SecretSource,
      scope: ORG_SCOPE,
      secret: null,
    })),
  ];
  return rows.sort((a, b) => a.name.localeCompare(b.name));
}

export const filterSecretRows = (
  rows: OrgSecretRow[],
  { search, type, scope, source }: SecretFilters
): OrgSecretRow[] => {
  const needle = search.trim().toLowerCase();
  return rows.filter(
    (row) =>
      (!needle || row.name.toLowerCase().includes(needle)) &&
      (type === 'all' || row.kind === type) &&
      (scope === 'all' || scopeTarget(row.scope) === scope) &&
      (source === 'all' || row.source === source)
  );
};

export const isFiltered = (filters: SecretFilters) =>
  filters.search !== '' ||
  filters.type !== 'all' ||
  filters.scope !== 'all' ||
  filters.source !== 'all';

export const countKinds = (rows: OrgSecretRow[]) => ({
  secrets: rows.filter((row) => row.kind === 'secret').length,
  variables: rows.filter((row) => row.kind === 'variable').length,
});
